// The content uploader for a USB or disc duplication line (ADR-021 §3): the
// files copied onto every unit, picked as a folder, as files, or by drag and
// drop, and sent browser-direct to the media store in resumable blocks.
//
// It replaces the generic UploadSlot for `media_content_file_reference` because
// the content is not artwork: thousands of files, up to 256 GB, folder structure
// preserved, never an Asset row. Readiness for the price and submit gates is the
// line's latest session reaching `complete` (useMediaContentState).

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  MAX_MEDIA_CONTENT_BYTES,
  MAX_MEDIA_CONTENT_FILES,
  MEDIA_CONTENT_MAX_SEGMENT_LENGTH,
  MEDIA_CONTENT_RETENTION_DAYS,
  type FieldValues,
  type MediaContentQuoteStateResponse,
  type MediaContentSas,
  type MediaContentSessionDto,
  type MediaContentSessionStateResponse,
} from 'shared';

import { extractApiError } from '../api/client.js';
import * as mediaContentApi from '../api/media-content.js';
import type { FinalizeMismatch } from '../api/media-content.js';
import {
  formatByteRate,
  formatDecimalBytes,
  formatDuration,
  formatNumber,
} from '../i18n/formatters.js';
import { MediaCapacityAdvisory } from './MediaCapacityAdvisory.js';
import { Modal } from './Modal.js';
import { UploadIcon } from './UploadSlot.js';
import {
  clearActiveHeartbeat,
  clearResumeRecord,
  fileMatchKey,
  findCaseCollision,
  normalizeSelection,
  pickedFromDataTransfer,
  pickedFromFileList,
  readActiveHeartbeat,
  readResumeRecord,
  selectionLabel,
  summarizeSelection,
  TooManyFilesError,
  writeActiveHeartbeat,
  writeResumeRecord,
  type PickedFile,
  type SelectionSummary,
} from './mediaContentFiles.js';
import {
  MediaContentUploadEngine,
  UploadEngineError,
  type UploadJob,
  type UploadProgress,
} from './mediaContentUpload.js';
import {
  mediaContentLineState,
  mediaContentStateKey,
  useMediaContentQuoteState,
} from './useMediaContentState.js';

type Selection = { files: PickedFile[]; summary: SelectionSummary };

// A paused session the new selection matches file for file, so only what the
// store does not already hold is sent.
type ResumePlan = { session_id: string; state: MediaContentSessionStateResponse };

type Phase =
  | { kind: 'idle' }
  | { kind: 'review' }
  | { kind: 'starting' }
  | { kind: 'uploading' }
  | { kind: 'finalizing' }
  | { kind: 'mismatch'; session_id: string; mismatch: FinalizeMismatch }
  | { kind: 'failed'; session_id: string | null; message: string };

const ANNOUNCE_STEP_PERCENT = 10;
const MISMATCH_LIST_MAX = 20;
const ACTIVE_HEARTBEAT_MS = 5_000;
const ACTIVE_HEARTBEAT_STALE_MS = 15_000;
const ACTIVE_ELSEWHERE_POLL_MS = 5_000;

// A session file the server recorded that this tab cannot pair with a picked
// file at the same path and size. Never guessed: sending the wrong bytes under
// a path would pass finalize's size check and corrupt every copy.
class LocalMismatchError extends Error {
  constructor() {
    super('local_mismatch');
  }
}

function percentOf(p: UploadProgress): number {
  return p.bytes_total > 0 ? Math.min(100, Math.floor((p.bytes_done / p.bytes_total) * 100)) : 0;
}

export function MediaContentUploader({
  quoteId,
  quoteLineId,
  config,
  label,
  help,
  domId,
  error,
  disabled,
  capacityLabel,
  persistChoice,
}: {
  quoteId: string;
  quoteLineId: string;
  config: FieldValues;
  label: string;
  help: string | null;
  domId: string;
  error?: string;
  disabled: boolean;
  capacityLabel: (value: string) => string;
  // Saves the order when the stored line does not yet hold the "Upload them
  // here" answer this uploader is shown for. The session route reads the STORED
  // choice, and a rep's edits wait for their own Save, so a new upload persists
  // the on-screen choice first. Resolves false when the save was refused.
  persistChoice: () => Promise<boolean>;
}): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  const qc = useQueryClient();
  const stateQuery = useMediaContentQuoteState(quoteId);
  const latest = mediaContentLineState(stateQuery.data, quoteLineId)?.latest_session ?? null;

  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [selection, setSelection] = useState<Selection | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);
  const [resumePlan, setResumePlan] = useState<ResumePlan | null>(null);
  const [differsFromPaused, setDiffersFromPaused] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [paused, setPaused] = useState(false);
  const [announced, setAnnounced] = useState(0);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [busyCancel, setBusyCancel] = useState(false);
  const [matching, setMatching] = useState(false);

  const engineRef = useRef<MediaContentUploadEngine | null>(null);
  const pickTokenRef = useRef(0);
  const sessionFilesRef = useRef<Map<string, PickedFile>>(new Map());
  const folderInputRef = useRef<HTMLInputElement>(null);
  const filesInputRef = useRef<HTMLInputElement>(null);

  const pausedSessionId = latest?.status === 'uploading' ? latest.session_id : null;
  const resumeRecord = useMemo(
    () => (pausedSessionId ? readResumeRecord(quoteLineId) : null),
    [pausedSessionId, quoteLineId]
  );
  const [now, setNow] = useState(() => Date.now());
  const engineIdle = phase.kind === 'idle' || phase.kind === 'review';
  useEffect(() => {
    if (!pausedSessionId || !engineIdle) return;
    const timer = setInterval(() => setNow(Date.now()), ACTIVE_ELSEWHERE_POLL_MS);
    return () => clearInterval(timer);
  }, [pausedSessionId, engineIdle]);
  const heartbeat = pausedSessionId && engineIdle ? readActiveHeartbeat(pausedSessionId) : null;
  const activeElsewhere = heartbeat !== null && now - heartbeat < ACTIVE_HEARTBEAT_STALE_MS;

  useEffect(() => {
    folderInputRef.current?.setAttribute('webkitdirectory', '');
  }, []);

  useEffect(() => () => engineRef.current?.cancel(), []);

  const transferring =
    phase.kind === 'starting' || phase.kind === 'uploading' || phase.kind === 'finalizing';
  useEffect(() => {
    if (!transferring) return;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [transferring]);

  function patchLatestSession(session: MediaContentSessionDto): void {
    qc.setQueryData<MediaContentQuoteStateResponse>(mediaContentStateKey(quoteId), (old) => {
      const lines = old?.lines ?? [];
      const found = lines.some((l) => l.quote_line_id === quoteLineId);
      return {
        lines: found
          ? lines.map((l) =>
              l.quote_line_id === quoteLineId ? { ...l, latest_session: session } : l
            )
          : [
              ...lines,
              { quote_line_id: quoteLineId, latest_session: session, latest_link_check: null },
            ],
      };
    });
    void qc.invalidateQueries({ queryKey: mediaContentStateKey(quoteId) });
  }

  function engineFailureMessage(err: unknown): string {
    if (err instanceof UploadEngineError && err.code === 'session_closed') {
      return t('mediaContent.upload.error.sessionClosed');
    }
    if (err instanceof UploadEngineError && err.code === 'unreadable') {
      return t('mediaContent.upload.error.unreadable');
    }
    if (err instanceof LocalMismatchError) {
      return t('mediaContent.upload.error.localMismatch');
    }
    if (err instanceof UploadEngineError && err.code === 'rejected') {
      return t('mediaContent.upload.error.rejected');
    }
    const api = extractApiError(err);
    return api?.message ?? t('mediaContent.upload.error.network');
  }

  async function matchPausedSession(
    files: PickedFile[],
    sessionId: string,
    token: number
  ): Promise<void> {
    setMatching(true);
    try {
      const state = await mediaContentApi.getMediaContentSessionState(sessionId);
      if (token !== pickTokenRef.current) return;
      if (state.session.status !== 'uploading') {
        setResumePlan(null);
        return;
      }
      const byKey = new Map(
        files.map((f) => [fileMatchKey(f.relative_path, f.file.size, f.file.lastModified), f])
      );
      const byPath = new Map(files.map((f) => [f.relative_path, f]));
      const matched = new Map<string, PickedFile>();
      for (const sf of state.files) {
        const hit =
          sf.last_modified === null
            ? byPath.get(sf.relative_path)
            : byKey.get(fileMatchKey(sf.relative_path, sf.size_bytes, sf.last_modified));
        if (!hit || hit.file.size !== sf.size_bytes) break;
        matched.set(sf.relative_path, hit);
      }
      if (matched.size === state.files.length && files.length === state.files.length) {
        sessionFilesRef.current = matched;
        setResumePlan({ session_id: sessionId, state });
        setDiffersFromPaused(false);
      } else {
        setResumePlan(null);
        setDiffersFromPaused(true);
      }
    } catch {
      if (token === pickTokenRef.current) setResumePlan(null);
    } finally {
      if (token === pickTokenRef.current) setMatching(false);
    }
  }

  function acceptPicked(picked: PickedFile[]): void {
    const token = (pickTokenRef.current += 1);
    setPickError(null);
    setMatching(false);
    if (picked.length === 0) return;
    const { files, rejected } = normalizeSelection(picked);
    if (rejected) {
      setPickError(
        t('mediaContent.upload.limit.badPath', {
          path: rejected.path,
          reason: t(`mediaContent.upload.pathReason.${rejected.reason}`, {
            max: MEDIA_CONTENT_MAX_SEGMENT_LENGTH,
          }),
        })
      );
      return;
    }
    if (files.length === 0) return;
    setSelection({ files, summary: summarizeSelection(files) });
    setResumePlan(null);
    setDiffersFromPaused(false);
    setPhase({ kind: 'review' });
    if (latest?.status === 'uploading') void matchPausedSession(files, latest.session_id, token);
  }

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setDragging(false);
    if (disabled || transferring) return;
    pickedFromDataTransfer(e.dataTransfer)
      .then(acceptPicked)
      .catch((err: unknown) => {
        setPickError(
          err instanceof TooManyFilesError
            ? t('mediaContent.upload.limit.tooManyFiles', {
                max: formatNumber(MAX_MEDIA_CONTENT_FILES),
              })
            : t('mediaContent.upload.error.readDrop')
        );
      });
  }

  const validation = useMemo(() => {
    if (!selection) return null;
    const { summary, files } = selection;
    if (summary.file_count > MAX_MEDIA_CONTENT_FILES) {
      return t('mediaContent.upload.limit.tooManyFiles', {
        max: formatNumber(MAX_MEDIA_CONTENT_FILES),
      });
    }
    if (summary.total_bytes > MAX_MEDIA_CONTENT_BYTES) {
      return t('mediaContent.upload.limit.tooLarge', {
        max: formatDecimalBytes(MAX_MEDIA_CONTENT_BYTES),
      });
    }
    const collision = findCaseCollision(files);
    if (collision) {
      return t('mediaContent.upload.limit.caseCollision', {
        first: collision[0],
        second: collision[1],
      });
    }
    return null;
  }, [selection, t]);

  async function runEngine(args: {
    session_id: string;
    sas: MediaContentSas;
    block_size: number;
    jobs: UploadJob[];
    already_bytes: number;
    already_files: number;
    total_bytes: number;
    total_files: number;
  }): Promise<void> {
    const engine = new MediaContentUploadEngine({
      sas: args.sas,
      block_size: args.block_size,
      jobs: args.jobs,
      already_bytes: args.already_bytes,
      already_files: args.already_files,
      total_bytes: args.total_bytes,
      total_files: args.total_files,
      refreshSas: () => mediaContentApi.refreshMediaContentSas(args.session_id),
      onProgress: setProgress,
    });
    engineRef.current = engine;
    setPaused(false);
    setAnnounced(0);
    setPhase({ kind: 'uploading' });
    writeActiveHeartbeat(args.session_id);
    const beat = setInterval(() => writeActiveHeartbeat(args.session_id), ACTIVE_HEARTBEAT_MS);
    try {
      await engine.run();
    } catch (err) {
      if (engineRef.current !== engine) return;
      engineRef.current = null;
      if (err instanceof UploadEngineError && err.code === 'cancelled') return;
      setPhase({ kind: 'failed', session_id: args.session_id, message: engineFailureMessage(err) });
      return;
    } finally {
      clearInterval(beat);
      clearActiveHeartbeat(args.session_id);
    }
    if (engineRef.current !== engine) return;
    engineRef.current = null;
    await finalize(args.session_id);
  }

  async function finalize(session_id: string): Promise<void> {
    setPhase({ kind: 'finalizing' });
    try {
      const result = await mediaContentApi.finalizeMediaContentSession(session_id);
      if (!result.ok) {
        setPhase({ kind: 'mismatch', session_id, mismatch: result });
        return;
      }
      clearResumeRecord(quoteLineId);
      patchLatestSession(result.session);
      sessionFilesRef.current = new Map();
      setSelection(null);
      setResumePlan(null);
      setProgress(null);
      setPhase({ kind: 'idle' });
    } catch (err) {
      setPhase({ kind: 'failed', session_id, message: engineFailureMessage(err) });
    }
  }

  async function startNew(sel: Selection): Promise<void> {
    setProgress(null);
    setPhase({ kind: 'starting' });
    try {
      if (!(await persistChoice())) {
        setPhase({
          kind: 'failed',
          session_id: null,
          message: t('mediaContent.upload.error.notSaved'),
        });
        return;
      }
      const created = await mediaContentApi.createMediaContentSession({
        quote_id: quoteId,
        quote_line_id: quoteLineId,
        files: sel.files.map((f) => ({
          relative_path: f.relative_path,
          size_bytes: f.file.size,
          last_modified: f.file.lastModified,
        })),
      });
      const byPath = new Map(sel.files.map((f) => [f.relative_path, f]));
      const map = new Map<string, PickedFile>();
      for (const sf of created.files) {
        const hit = byPath.get(sf.relative_path);
        if (!hit || hit.file.size !== sf.size_bytes) throw new LocalMismatchError();
        map.set(sf.relative_path, hit);
      }
      sessionFilesRef.current = map;
      writeResumeRecord({
        session_id: created.session.session_id,
        quote_line_id: quoteLineId,
        label: selectionLabel(sel.summary),
        file_count: sel.summary.file_count,
      });
      patchLatestSession(created.session);
      const jobs: UploadJob[] = [];
      for (const sf of created.files) {
        const picked = map.get(sf.relative_path);
        if (!picked || sf.committed) continue;
        jobs.push({
          file: picked.file,
          relative_path: sf.relative_path,
          staged_block_ids: new Set(),
        });
      }
      await runEngine({
        session_id: created.session.session_id,
        sas: created.sas,
        block_size: created.session.block_size_bytes,
        jobs,
        already_bytes: 0,
        already_files: created.files.length - jobs.length,
        total_bytes: created.session.total_bytes,
        total_files: created.session.file_count,
      });
    } catch (err) {
      setPhase({ kind: 'failed', session_id: null, message: engineFailureMessage(err) });
    }
  }

  async function continueSession(plan: ResumePlan, only?: ReadonlySet<string>): Promise<void> {
    setProgress(null);
    setPhase({ kind: 'starting' });
    try {
      const { state } = plan;
      const sas = await mediaContentApi.refreshMediaContentSas(plan.session_id);
      const jobs: UploadJob[] = [];
      let alreadyBytes = 0;
      let alreadyFiles = 0;
      for (const sf of state.files) {
        const forced = only?.has(sf.relative_path) ?? false;
        if (sf.committed && !forced) {
          alreadyBytes += sf.size_bytes;
          alreadyFiles += 1;
          continue;
        }
        const picked = sessionFilesRef.current.get(sf.relative_path);
        if (!picked || picked.file.size !== sf.size_bytes) throw new LocalMismatchError();
        jobs.push({
          file: picked.file,
          relative_path: sf.relative_path,
          staged_block_ids: forced ? new Set() : new Set(sf.uncommitted_block_ids),
        });
      }
      await runEngine({
        session_id: plan.session_id,
        sas,
        block_size: state.session.block_size_bytes,
        jobs,
        already_bytes: alreadyBytes,
        already_files: alreadyFiles,
        total_bytes: state.session.total_bytes,
        total_files: state.session.file_count,
      });
    } catch (err) {
      setPhase({ kind: 'failed', session_id: plan.session_id, message: engineFailureMessage(err) });
    }
  }

  function start(): void {
    if (!selection || validation || matching) return;
    if (resumePlan) void continueSession(resumePlan);
    else void startNew(selection);
  }

  function retryFailed(session_id: string | null): void {
    if (session_id === null) {
      if (selection) void startNew(selection);
      else setPhase({ kind: 'idle' });
      return;
    }
    void mediaContentApi
      .getMediaContentSessionState(session_id)
      .then((state) => {
        if (state.session.status !== 'uploading') {
          setPhase({ kind: 'idle' });
          void qc.invalidateQueries({ queryKey: mediaContentStateKey(quoteId) });
          return;
        }
        if (state.files.every((f) => f.committed)) return finalize(session_id);
        return continueSession({ session_id, state });
      })
      .catch((err: unknown) =>
        setPhase({ kind: 'failed', session_id, message: engineFailureMessage(err) })
      );
  }

  function resendMismatched(session_id: string, mismatch: FinalizeMismatch): void {
    const paths = new Set([
      ...mismatch.missing,
      ...mismatch.size_mismatch.map((m) => m.relative_path),
    ]);
    const allPresent = [...paths].every((p) => sessionFilesRef.current.has(p));
    if (!allPresent) {
      setSelection(null);
      setPickError(t('mediaContent.upload.mismatch.reselect'));
      setPhase({ kind: 'idle' });
      void qc.invalidateQueries({ queryKey: mediaContentStateKey(quoteId) });
      return;
    }
    void mediaContentApi
      .getMediaContentSessionState(session_id)
      .then((state) => continueSession({ session_id, state }, paths))
      .catch((err: unknown) =>
        setPhase({ kind: 'failed', session_id, message: engineFailureMessage(err) })
      );
  }

  async function cancelUpload(): Promise<void> {
    const sessionId =
      phase.kind === 'mismatch' || phase.kind === 'failed'
        ? (phase.session_id ?? latest?.session_id ?? null)
        : (latest?.session_id ?? null);
    setBusyCancel(true);
    engineRef.current?.cancel();
    engineRef.current = null;
    try {
      if (sessionId) await mediaContentApi.cancelMediaContentSession(sessionId);
    } catch {
      // The session expires on its own; the customer's intent is already honoured locally.
    } finally {
      setBusyCancel(false);
      setConfirmCancel(false);
      clearResumeRecord(quoteLineId);
      sessionFilesRef.current = new Map();
      setSelection(null);
      setResumePlan(null);
      setProgress(null);
      setPhase({ kind: 'idle' });
      void qc.invalidateQueries({ queryKey: mediaContentStateKey(quoteId) });
    }
  }

  function togglePause(): void {
    const engine = engineRef.current;
    if (!engine) return;
    if (paused) engine.resume();
    else engine.pause();
    setPaused(!paused);
  }

  const pct = progress ? percentOf(progress) : 0;
  useEffect(() => {
    const step = Math.floor(pct / ANNOUNCE_STEP_PERCENT) * ANNOUNCE_STEP_PERCENT;
    if (step !== announced) setAnnounced(step);
  }, [pct, announced]);

  const pickers = (
    <>
      <input
        ref={folderInputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          acceptPicked(pickedFromFileList(e.target.files));
          e.target.value = '';
        }}
      />
      <input
        ref={filesInputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          acceptPicked(pickedFromFileList(e.target.files));
          e.target.value = '';
        }}
      />
    </>
  );

  const dropzone = (
    <div
      className={
        'artwork-uploader__dropzone media-content__dropzone' +
        (dragging ? ' artwork-uploader__dropzone--active' : '')
      }
      role="group"
      aria-label={t('mediaContent.upload.dropzoneAria')}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <span className="artwork-uploader__dropzone-icon" aria-hidden="true">
        <UploadIcon />
      </span>
      <span className="artwork-uploader__dropzone-text">
        <strong>{t('mediaContent.upload.dropPrompt')}</strong>
        <span className="media-content__actions">
          <button
            type="button"
            className="primary"
            disabled={disabled}
            onClick={() => folderInputRef.current?.click()}
          >
            {t('mediaContent.upload.chooseFolder')}
          </button>
          <button type="button" disabled={disabled} onClick={() => filesInputRef.current?.click()}>
            {t('mediaContent.upload.chooseFiles')}
          </button>
        </span>
      </span>
    </div>
  );

  let body: JSX.Element;
  if (phase.kind === 'review' && selection) {
    body = (
      <div className="media-content__panel">
        <SelectionSummaryView summary={selection.summary} />
        {validation && (
          <p className="field-error" role="alert">
            {validation}
          </p>
        )}
        {resumePlan && (
          <p className="help">
            {t('mediaContent.upload.resumeMatched', {
              count: resumePlan.state.files.filter((f) => !f.committed).length,
            })}
          </p>
        )}
        {differsFromPaused && <p className="help">{t('mediaContent.upload.resumeDiffers')}</p>}
        {!resumePlan && latest?.status === 'complete' && (
          <p className="help">
            {t('mediaContent.upload.replaceWarning', { count: latest.file_count })}
          </p>
        )}
        <MediaCapacityAdvisory
          config={config}
          sizes={{
            session_total_bytes: selection.summary.total_bytes,
            session_largest_file_bytes: selection.summary.largest_file_bytes,
          }}
          capacityLabel={capacityLabel}
        />
        <div className="media-content__actions">
          <button
            type="button"
            className="primary"
            disabled={Boolean(validation) || disabled || matching}
            onClick={start}
          >
            {matching
              ? t('mediaContent.upload.matching')
              : resumePlan
                ? t('mediaContent.upload.continue')
                : t('mediaContent.upload.start')}
          </button>
          <button
            type="button"
            onClick={() => {
              setSelection(null);
              setResumePlan(null);
              setPhase({ kind: 'idle' });
            }}
          >
            {t('mediaContent.upload.chooseDifferent')}
          </button>
        </div>
      </div>
    );
  } else if (
    phase.kind === 'starting' ||
    phase.kind === 'uploading' ||
    phase.kind === 'finalizing'
  ) {
    const p = progress;
    body = (
      <div className="media-content__panel">
        <div
          className="media-content__bar"
          role="progressbar"
          aria-label={t('mediaContent.upload.progressAria')}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
        >
          <div className="media-content__bar-fill" style={{ width: `${pct}%` }} />
        </div>
        <p className="media-content__stats">
          <strong>
            {phase.kind === 'finalizing'
              ? t('mediaContent.upload.verifying')
              : phase.kind === 'starting'
                ? t('mediaContent.upload.preparing')
                : paused
                  ? t('mediaContent.upload.pausedAt', { percent: formatNumber(pct) })
                  : t('mediaContent.upload.percent', { percent: formatNumber(pct) })}
          </strong>
          {p && (
            <span className="help">
              {t('mediaContent.upload.filesProgress', {
                done: formatNumber(p.files_done),
                total: formatNumber(p.files_total),
                sent: formatDecimalBytes(p.bytes_done),
                size: formatDecimalBytes(p.bytes_total),
              })}
            </span>
          )}
          {p && phase.kind === 'uploading' && !paused && p.bytes_per_second !== null && (
            <span className="help">
              {p.eta_seconds !== null
                ? t('mediaContent.upload.rateEta', {
                    rate: formatByteRate(p.bytes_per_second),
                    eta: formatDuration(p.eta_seconds),
                  })
                : formatByteRate(p.bytes_per_second)}
            </span>
          )}
        </p>
        <p className="media-content__live" aria-live="polite" aria-atomic="true">
          {phase.kind === 'uploading'
            ? t('mediaContent.upload.percent', { percent: formatNumber(announced) })
            : ''}
        </p>
        {phase.kind === 'uploading' && (
          <div className="media-content__actions">
            <button type="button" onClick={togglePause}>
              {paused ? t('mediaContent.upload.resume') : t('mediaContent.upload.pause')}
            </button>
            <button
              type="button"
              className="link link--danger"
              onClick={() => setConfirmCancel(true)}
            >
              {t('mediaContent.upload.cancel')}
            </button>
          </div>
        )}
        <p className="help">{t('mediaContent.upload.keepOpen')}</p>
      </div>
    );
  } else if (phase.kind === 'mismatch') {
    const rows = [
      ...phase.mismatch.missing.map((p) =>
        t('mediaContent.upload.mismatch.missingRow', { path: p })
      ),
      ...phase.mismatch.size_mismatch.map((m) =>
        t('mediaContent.upload.mismatch.sizeRow', {
          path: m.relative_path,
          expected: formatDecimalBytes(m.expected),
          actual: formatDecimalBytes(m.actual),
        })
      ),
    ];
    const { session_id, mismatch } = phase;
    body = (
      <div className="media-content__panel" role="alert">
        <p>
          <strong>{t('mediaContent.upload.mismatch.title', { count: rows.length })}</strong>
        </p>
        <ul className="media-content__paths">
          {rows.slice(0, MISMATCH_LIST_MAX).map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
        {rows.length > MISMATCH_LIST_MAX && (
          <p className="help">
            {t('mediaContent.upload.andMore', { count: rows.length - MISMATCH_LIST_MAX })}
          </p>
        )}
        {mismatch.size_mismatch.length > 0 && (
          <p className="help">{t('mediaContent.upload.mismatch.changedHint')}</p>
        )}
        <div className="media-content__actions">
          <button
            type="button"
            className="primary"
            onClick={() => resendMismatched(session_id, mismatch)}
          >
            {t('mediaContent.upload.mismatch.resend')}
          </button>
          <button
            type="button"
            className="link link--danger"
            onClick={() => setConfirmCancel(true)}
          >
            {t('mediaContent.upload.cancel')}
          </button>
        </div>
      </div>
    );
  } else if (phase.kind === 'failed') {
    const { session_id, message } = phase;
    body = (
      <div className="media-content__panel">
        <p className="field-error" role="alert">
          {message}
        </p>
        <div className="media-content__actions">
          <button type="button" className="primary" onClick={() => retryFailed(session_id)}>
            {t('mediaContent.upload.retry')}
          </button>
          <button
            type="button"
            className="link link--danger"
            onClick={() => setConfirmCancel(true)}
          >
            {t('mediaContent.upload.cancel')}
          </button>
        </div>
      </div>
    );
  } else if (latest?.status === 'complete') {
    body = (
      <CompleteView
        session={latest}
        disabled={disabled}
        onReplace={() => folderInputRef.current?.click()}
        onReplaceFiles={() => filesInputRef.current?.click()}
      />
    );
  } else if (latest?.status === 'uploading') {
    const record = resumeRecord?.session_id === latest.session_id ? resumeRecord : null;
    body = activeElsewhere ? (
      <div className="media-content__panel">
        <p className="media-content__status" role="status">
          <strong>{t('mediaContent.upload.otherTab')}</strong>
        </p>
      </div>
    ) : (
      <div className="media-content__panel">
        <p className="media-content__status" role="status">
          <strong>
            {record?.label
              ? t('mediaContent.upload.pausedRemoteNamed', {
                  label: record.label,
                  count: record.file_count,
                  files: formatNumber(record.file_count),
                })
              : t('mediaContent.upload.pausedRemote')}
          </strong>
        </p>
        {!disabled && dropzone}
        {!disabled && (
          <div className="media-content__actions">
            <button
              type="button"
              className="link link--danger"
              onClick={() => setConfirmCancel(true)}
            >
              {t('mediaContent.upload.cancel')}
            </button>
          </div>
        )}
      </div>
    );
  } else if (stateQuery.isLoading) {
    body = <p className="help">{t('mediaContent.upload.loading')}</p>;
  } else if (disabled) {
    body = <p className="help">{t('mediaContent.upload.none')}</p>;
  } else {
    body = dropzone;
  }

  return (
    <section
      className="artwork-slot media-content"
      id={domId}
      tabIndex={-1}
      aria-invalid={error ? true : undefined}
    >
      <header className="artwork-slot__header">
        <h4 className="artwork-slot__title">
          {label}
          <span className="artwork-slot__required" aria-label={t('uploadSlot.header.requiredAria')}>
            {' '}
            *
          </span>
        </h4>
        {help && <p className="help artwork-slot__help">{help}</p>}
        {latest?.status !== 'complete' && !transferring && (
          <span className="badge badge--warn">{t('uploadSlot.header.neededBadge')}</span>
        )}
        {error && (
          <span className="field-error" role="alert">
            {error}
          </span>
        )}
      </header>
      {pickers}
      {pickError && (
        <p className="field-error" role="alert">
          {pickError}
        </p>
      )}
      {body}
      {phase.kind !== 'review' &&
        latest &&
        (latest.status === 'uploading' || latest.status === 'complete') && (
          <MediaCapacityAdvisory
            config={config}
            sizes={{
              session_total_bytes: latest.total_bytes,
              session_largest_file_bytes: latest.largest_file_bytes,
            }}
            capacityLabel={capacityLabel}
          />
        )}
      {confirmCancel && (
        <Modal
          title={t('mediaContent.upload.cancelConfirm.title')}
          onClose={() => {
            if (!busyCancel) setConfirmCancel(false);
          }}
          footer={
            <>
              <button
                type="button"
                onClick={() => {
                  if (!busyCancel) setConfirmCancel(false);
                }}
                aria-disabled={busyCancel}
              >
                {t('mediaContent.upload.cancelConfirm.keep')}
              </button>
              <button
                type="button"
                className="danger"
                aria-disabled={busyCancel}
                onClick={() => {
                  if (!busyCancel) void cancelUpload();
                }}
              >
                {t('mediaContent.upload.cancelConfirm.confirm')}
              </button>
            </>
          }
        >
          <p>{t('mediaContent.upload.cancelConfirm.body')}</p>
        </Modal>
      )}
    </section>
  );
}

function SelectionSummaryView({ summary }: { summary: SelectionSummary }): JSX.Element {
  const { t } = useTranslation('quote');
  const more = summary.file_count - summary.sample_paths.length;
  return (
    <div className="media-content__summary">
      <p>
        <strong>
          {t('mediaContent.upload.summaryTotals', {
            count: summary.file_count,
            files: formatNumber(summary.file_count),
            size: formatDecimalBytes(summary.total_bytes),
          })}
        </strong>
      </p>
      {summary.top_folders.length > 0 && (
        <p className="help">
          {t('mediaContent.upload.summaryFolders', {
            count: summary.top_folders.length,
            folders: summary.top_folders.join(', '),
          })}
        </p>
      )}
      <ul className="media-content__paths">
        {summary.sample_paths.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
      {more > 0 && <p className="help">{t('mediaContent.upload.andMore', { count: more })}</p>}
    </div>
  );
}

const COMPLETE_SAMPLE_SIZE = 20;

function CompleteView({
  session,
  disabled,
  onReplace,
  onReplaceFiles,
}: {
  session: MediaContentSessionDto;
  disabled: boolean;
  onReplace: () => void;
  onReplaceFiles: () => void;
}): JSX.Element {
  const { t } = useTranslation('quote');
  const [open, setOpen] = useState(false);
  const [paths, setPaths] = useState<string[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!open || paths !== null) return;
    let cancelled = false;
    mediaContentApi
      .getMediaContentSessionState(session.session_id)
      .then((state) => {
        if (!cancelled)
          setPaths(state.files.map((f) => f.relative_path).sort((a, b) => a.localeCompare(b)));
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, paths, session.session_id]);

  const shown = paths?.slice(0, COMPLETE_SAMPLE_SIZE) ?? [];
  return (
    <div className="media-content__panel">
      <p className="media-content__status" role="status">
        {t('mediaContent.upload.complete', {
          count: session.file_count,
          files: formatNumber(session.file_count),
          size: formatDecimalBytes(session.total_bytes),
          days: MEDIA_CONTENT_RETENTION_DAYS,
        })}
      </p>
      <div className="media-content__actions">
        <button
          type="button"
          className="link"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? t('mediaContent.upload.hideFiles') : t('mediaContent.upload.showFiles')}
        </button>
        {!disabled && (
          <>
            <button type="button" className="link" onClick={onReplace}>
              {t('mediaContent.upload.replaceFolder')}
            </button>
            <button type="button" className="link" onClick={onReplaceFiles}>
              {t('mediaContent.upload.replaceFiles')}
            </button>
          </>
        )}
      </div>
      {open && (
        <div className="media-content__summary">
          {loadFailed ? (
            <p className="help">{t('mediaContent.upload.filesUnavailable')}</p>
          ) : paths === null ? (
            <p className="help">{t('mediaContent.upload.loading')}</p>
          ) : (
            <>
              <ul className="media-content__paths">
                {shown.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
              {paths.length > shown.length && (
                <p className="help">
                  {t('mediaContent.upload.andMore', { count: paths.length - shown.length })}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
